// Temporary debug utility: measures time spent in labeled sections and logs aggregated stats.
// Usage:
//   const t = startTiming('TextBlock');
//   t.mark('hooks-start');
//   // ... hooks ...
//   t.mark('hooks-end');
//   // ... render ...
//   t.done();
//
// Every 2s logs: average, max, total ms per section + call count.

interface SectionStats {
  total: number;
  max: number;
  count: number;
}

const stats: Record<string, Record<string, SectionStats>> = {};
let timer: ReturnType<typeof setInterval> | null = null;

function ensureTimer() {
  if (timer) return;
  timer = setInterval(() => {
    const components = Object.keys(stats);
    if (components.length === 0) return;

    for (const comp of components) {
      const sections = stats[comp];
      const sectionNames = Object.keys(sections);
      if (sectionNames.length === 0) continue;

      const callCount = sections[sectionNames[0]]?.count ?? 0;
      if (callCount === 0) continue;

      const table: Record<string, { avg: string; max: string; total: string; calls: number }> = {};
      for (const name of sectionNames) {
        const s = sections[name];
        table[name] = {
          avg: (s.total / s.count).toFixed(3),
          max: s.max.toFixed(3),
          total: s.total.toFixed(1),
          calls: s.count,
        };
      }

      console.log(
        `%c[Timing] ${comp}`,
        'color: #38bdf8; font-weight: bold',
        table,
      );

      // reset
      for (const name of sectionNames) {
        sections[name] = { total: 0, max: 0, count: 0 };
      }
    }
  }, 2000);
}

export function startTiming(component: string) {
  ensureTimer();
  if (!stats[component]) stats[component] = {};

  const marks: { label: string; time: number }[] = [];
  const t0 = performance.now();
  marks.push({ label: '_start', time: t0 });

  return {
    mark(label: string) {
      marks.push({ label, time: performance.now() });
    },
    done() {
      const tEnd = performance.now();
      marks.push({ label: '_end', time: tEnd });

      const sections = stats[component];

      // record each pair of consecutive marks as a section
      for (let i = 0; i < marks.length - 1; i++) {
        const name = `${marks[i].label} → ${marks[i + 1].label}`;
        const dur = marks[i + 1].time - marks[i].time;
        if (!sections[name]) sections[name] = { total: 0, max: 0, count: 0 };
        sections[name].total += dur;
        sections[name].max = Math.max(sections[name].max, dur);
        sections[name].count += 1;
      }

      // record total
      const totalDur = tEnd - t0;
      if (!sections['TOTAL']) sections['TOTAL'] = { total: 0, max: 0, count: 0 };
      sections['TOTAL'].total += totalDur;
      sections['TOTAL'].max = Math.max(sections['TOTAL'].max, totalDur);
      sections['TOTAL'].count += 1;
    },
  };
}
